import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ShieldCheck, Award } from 'lucide-react';
import { fetchBySlug } from '../lib/api';

export default function ServiceDetail() {
  const { subService } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      // Map path slugs to CMS slugs
      const slugMap = {
        'haandvaerkergaranti': 'haandvaerkergaranti',
        'leverandoerer': 'leverandoerer'
      };
      const cmsSlug = slugMap[subService] || subService;
      const data = await fetchBySlug('da', cmsSlug);
      setPageData(data);
      setLoading(false);
    }
    loadData();
  }, [subService]);

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="animate-pulse text-sm font-semibold tracking-widest uppercase">Henter indhold...</div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white p-6 text-center">
        <h1 className="text-2xl font-bold uppercase tracking-wider mb-4">Siden blev ikke fundet</h1>
        <p className="text-gray-400 text-sm mb-6">Vi kunne ikke finde den ønskede ydelsesside.</p>
        <Link to="/ydelser" className="text-blue-500 hover:text-blue-400 font-bold uppercase tracking-wider text-xs flex items-center space-x-2">
          <ArrowLeft className="w-4 h-4" />
          <span>Tilbage til ydelser</span>
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title || `${pageData.title} – Møldrup VVS A/S`}</title>
        <meta name="description" content={pageData.meta_description || ''} />
        <html lang="da" />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative bg-slate-950 text-white py-24 md:py-32 border-b border-slate-900">
        <div className="absolute inset-0 z-0">
          {pageData.images && pageData.images.length > 0 && (
            <img
              src={pageData.images[0]}
              alt={pageData.title}
              className="w-full h-full object-cover opacity-10"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-12">
          <Link
            to="/ydelser"
            className="inline-flex items-center space-x-2 text-xs font-bold tracking-widest uppercase text-blue-500 hover:text-blue-400 mb-6 transition-colors duration-150"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Tilbage til ydelser</span>
          </Link>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-wider uppercase">
            {pageData.title}
          </h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white text-slate-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="max-w-none [&_p]:mb-6 [&_p]:text-slate-600 [&_p]:leading-relaxed [&_p]:text-base [&_h1]:text-3xl [&_h1]:font-extrabold [&_h1]:mt-8 [&_h1]:mb-4 [&_h1]:uppercase [&_h1]:tracking-wider [&_h1]:text-slate-900 [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:uppercase [&_h2]:tracking-wider [&_h2]:text-slate-900 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:uppercase [&_h3]:tracking-wider [&_h3]:text-slate-900 [&_ul]:grid [&_ul]:grid-cols-1 [&_ul]:sm:grid-cols-2 [&_ul]:md:grid-cols-3 [&_ul]:gap-x-8 [&_ul]:gap-y-3 [&_ul]:my-8 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-6 [&_ol]:space-y-2 [&_li]:text-slate-600 [&_li]:leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a]:font-semibold hover:[&_a]:text-blue-800 [&_a]:transition-colors [&_strong]:font-bold [&_strong]:text-slate-900">
            {/* Render HTML content verbatim from CMS */}
            <div ref={contentRef} dangerouslySetInnerHTML={{ __html: pageData.content }} />
          </div>

          {/* Render page images if any (e.g. guarantee logos or supplier logos) */}
          {pageData.images && pageData.images.length > 0 && (
            <div className="mt-16 pt-12 border-t border-gray-100 overflow-hidden">
              <h3 className="text-xs font-extrabold uppercase tracking-widest text-gray-400 mb-6">
                {subService === 'leverandoerer' ? 'Vores Leverandører' : 'Tilknyttede certifikater & logoer'}
              </h3>

              {subService === 'leverandoerer' ? (
                <div className="relative w-full overflow-hidden py-4 bg-slate-50/50 border-t border-b border-gray-100">
                  {/* Gradient Overlays for smooth fade-out on edges */}
                  <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
                  <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />

                  <style dangerouslySetInnerHTML={{__html: `
                    @keyframes marquee-detail {
                      0% { transform: translateX(0); }
                      100% { transform: translateX(-50%); }
                    }
                    .animate-marquee-detail {
                      display: flex;
                      width: max-content;
                      animation: marquee-detail 55s linear infinite;
                    }
                  `}} />

                  <div className="animate-marquee-detail gap-16 items-center">
                    {/* First set */}
                    {pageData.images.map((img, idx) => (
                      <div key={`set1-${idx}`} className="flex items-center justify-center w-36 h-16 shrink-0">
                        <img
                          src={img}
                          alt={`Leverandør logo ${idx + 1}`}
                          className="max-w-full max-h-12 object-contain"
                        />
                      </div>
                    ))}
                    {/* Second set for infinite loop */}
                    {pageData.images.map((img, idx) => (
                      <div key={`set2-${idx}`} className="flex items-center justify-center w-36 h-16 shrink-0">
                        <img
                          src={img}
                          alt={`Leverandør logo ${idx + 1}`}
                          className="max-w-full max-h-12 object-contain"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-6">
                  {pageData.images.map((img, idx) => {
                    const isHaandvaerkergaranti = img.includes('a81b6ce7-e3ad-412c-bcb0-79943202b635.png');
                    const imgElement = (
                      <img
                        src={img}
                        alt={`${pageData.title} logo ${idx + 1}`}
                        className="h-16 md:h-20 w-auto object-contain"
                      />
                    );

                    return (
                      <div key={idx} className="bg-white p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-150">
                        {isHaandvaerkergaranti ? (
                          <a
                            href="https://www.xn--hndvrkergaranti-hlbu.dk/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block hover:opacity-80 transition-opacity duration-150"
                          >
                            {imgElement}
                          </a>
                        ) : (
                          imgElement
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
